import { Isuccess } from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { FlyingMailClient } from '../../utils/flying-mail-client';

/**
 * Interface representing a mail permission.
 */
export interface ImailPermission {
  id: number;
  userId: string | number;
  companyId: string | number;
  mainAccess: boolean;
  read: boolean;
  write: boolean;
  createdAt: Date;
  updatedAt: Date;
  domainId: number;
}

/**
 * Class representing a mail permission.
 */
export class MailPermission {
  /**
   * Base URL for mail permission API endpoints.
   */
  static baseUrl = '/permissions';

  /**
   * The ID of the permission.
   */
  id: number;

  /**
   * The ID of the user associated with the permission.
   */
  userId: string | number;

  /**
   * The ID of the company associated with the permission.
   */
  companyId: string | number;

  /**
   * Whether the user has main access to the mail.
   */
  mainAccess: boolean;

  /**
   * Whether the user has read access to the mail.
   */
  read: boolean;

  /**
   * Whether the user has write access to the mail.
   */
  write: boolean;

  /**
   * The date and time the permission was created.
   */
  createdAt: Date;

  /**
   * The date and time the permission was last updated.
   */
  updatedAt: Date;

  /**
   * The ID of the domain associated with the permission.
   */
  domainId: number;

  constructor(data: ImailPermission) {
    this.id = data.id;
    this.userId = data.userId;
    this.companyId = data.companyId;
    this.mainAccess = data.mainAccess;
    this.read = data.read;
    this.write = data.write;
    this.createdAt = data.createdAt;
    this.updatedAt = data.updatedAt;
    this.domainId = data.domainId;
  }

  /**
   * Retrieves mail permissions for a specific user.
   *
   * @param userId - The ID of the user to retrieve permissions for.
   * @returns A Promise that resolves to a new MailPermission instance.
   */
  static async getByUser(userId: string | number) {
    const observer$ = FlyingMailClient.ehttp
      .makeGet<ImailPermission>(`${MailPermission.baseUrl}/user/${userId}`);
    const data = await lastValueFrom(observer$);

    return new MailPermission(data);
  }

  /**
   * Adds or updates mail permissions for a specific user.
   *
   * @param permissions - The permissions to add or update.
   * @returns A Promise that resolves to an Isuccess instance.
   */
  static addUpdate(permissions: Omit<ImailPermission, 'id' | 'createdAt' | 'updatedAt' | 'companyId'>) {
    const observer$ = FlyingMailClient.ehttp
      .makePut<Isuccess>(`${MailPermission.baseUrl}/${permissions.userId}`, permissions);

    return lastValueFrom(observer$);
  }

  /**
   * Removes the mail permission.
   *
   * @returns A Promise that resolves to an Isuccess instance.
   */
  remove() {
    return FlyingMailClient.ehttp
      .makeDelete<Isuccess>(`${MailPermission.baseUrl}/${this.userId}`);
  }
}
